import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { FieldErrors, FieldValues, UseFormReturn } from 'react-hook-form';
import { useBlocker } from 'react-router';
import { colours } from '../../theme';

/**
 * Unsaved changes, app-wide. Each form says whether it has changes and how to save or drop
 * them; before a dialog closes, a tab changes or the page changes, anything unsaved there is
 * offered for saving: Save, Discard, or Keep editing.
 *
 *   useUnsavedChanges({ dirty: form.formState.isDirty, save, discard: () => form.reset() });
 *   const guard = useLeaveGuard();
 *   <Dialog onClose={() => guard.close(close)} …>
 */

type Owner = string;
interface Entry {
  owner: Owner;
  /** In a tab's own content: it disappears when the tab changes. */
  tab: boolean;
  dirty: boolean;
  save(): Promise<boolean>;
  discard(): void;
}
type Choice = 'save' | 'discard' | 'stay';
type Reason = 'close' | 'tab' | 'leave';

interface Registry {
  set(id: string, e: Entry): void;
  remove(id: string): void;
  /** Unsaved entries, all of them or for one dialog (and optionally only its tab forms). */
  dirty(owner?: Owner, tabOnly?: boolean): Entry[];
  /** Asks what to do with them; resolves true when it's fine to go on. */
  resolve(entries: Entry[], reason: Reason): Promise<boolean>;
}

const RegistryCtx = createContext<Registry | null>(null);
/** Which dialog a form is in ('page' for forms on a page). DialogProvider sets one per dialog. */
export const OwnerCtx = createContext<Owner>('page');

const WORDING: Record<Reason, { save: string; discard: string; lead: string }> = {
  close: { save: 'Save and close', discard: 'Discard and close', lead: 'If you close now, your changes will be lost.' },
  tab: { save: 'Save and continue', discard: 'Discard and continue', lead: "If you switch now, the changes on this tab will be lost." },
  leave: { save: 'Save and leave', discard: 'Discard and leave', lead: 'If you leave this page now, your changes will be lost.' },
};

/** Keeps the list of forms with changes, and shows the "unsaved changes" question. */
export function UnsavedProvider({ children }: { children: ReactNode }) {
  const entries = useRef(new Map<string, Entry>());
  const [ask, setAsk] = useState<{ reason: Reason; answer(c: Choice): void } | null>(null);
  const [saving, setSaving] = useState(false);

  const registry = useMemo<Registry>(
    () => ({
      set: (id, e) => void entries.current.set(id, e),
      remove: (id) => void entries.current.delete(id),
      dirty: (owner, tabOnly) => [...entries.current.values()].filter((e) => e.dirty && (owner === undefined || e.owner === owner) && (!tabOnly || e.tab)),
      async resolve(list, reason) {
        if (!list.length) return true;
        const choice = await new Promise<Choice>((answer) => setAsk({ reason, answer }));
        setAsk(null);
        if (choice === 'stay') return false;
        if (choice === 'discard') {
          list.forEach((e) => e.discard());
          return true;
        }
        // Save each in turn; stop at the first that can't (its problem shows on the form).
        setSaving(true);
        try {
          for (const e of list) if (!(await e.save())) return false;
          return true;
        } finally {
          setSaving(false);
        }
      },
    }),
    [],
  );

  // Refreshing or closing the browser tab: browsers only allow their own "Leave site?" box.
  useEffect(() => {
    const onBeforeUnload = (ev: BeforeUnloadEvent) => {
      if (registry.dirty().length) {
        ev.preventDefault();
        ev.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [registry]);

  const words = ask ? WORDING[ask.reason] : WORDING.close;
  return (
    <RegistryCtx.Provider value={registry}>
      {children}
      <Dialog open={!!ask || saving} onClose={() => ask?.answer('stay')} maxWidth="sm" fullWidth aria-labelledby="unsaved-title" aria-describedby="unsaved-text">
        <DialogTitle id="unsaved-title">You have unsaved changes.</DialogTitle>
        <DialogContent>
          <DialogContentText id="unsaved-text" sx={{ color: 'text.secondary' }}>
            {words.lead}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1.5, '& > :not(style) ~ :not(style)': { ml: 0 }, '& > button': { whiteSpace: 'nowrap', flexShrink: 0 } }}>
          <Button onClick={() => ask?.answer('stay')} disabled={saving} sx={{ mr: 'auto' }}>
            Keep editing
          </Button>
          <Button onClick={() => ask?.answer('discard')} disabled={saving} sx={{ color: colours.faultRed, borderColor: colours.faultRed, '&:hover': { borderColor: colours.faultRed, bgcolor: 'rgba(180, 35, 24, 0.06)' } }}>
            {words.discard}
          </Button>
          <Button variant="contained" onClick={() => ask?.answer('save')} disabled={saving} autoFocus>
            {saving ? 'Saving…' : words.save}
          </Button>
        </DialogActions>
      </Dialog>
    </RegistryCtx.Provider>
  );
}

/**
 * Registers a form's unsaved changes while it's on screen. `save` resolves true when saved
 * (false when it couldn't be: a validation or server problem, which the form shows).
 */
export function useUnsavedChanges(o: { dirty: boolean; save(): Promise<boolean>; discard(): void; tab?: boolean }) {
  const registry = useContext(RegistryCtx);
  const owner = useContext(OwnerCtx);
  const id = useId();
  const latest = useRef(o);
  latest.current = o;
  // Kept current after every render, so the registry has the latest dirty state.
  useEffect(() => {
    registry?.set(id, { owner, tab: !!o.tab, dirty: o.dirty, save: () => latest.current.save(), discard: () => latest.current.discard() });
  });
  useEffect(() => () => registry?.remove(id), [registry, id]);
}

/** For closing a dialog or switching its tabs: asks first when something here is unsaved. */
export function useLeaveGuard() {
  const registry = useContext(RegistryCtx);
  const owner = useContext(OwnerCtx);
  const busy = useRef(false);
  const run = useCallback(
    async (reason: Reason, go: () => void, tabOnly = false) => {
      if (!registry) return go();
      if (busy.current) return;
      busy.current = true;
      try {
        if (await registry.resolve(registry.dirty(owner, tabOnly), reason)) go();
      } finally {
        busy.current = false;
      }
    },
    [registry, owner],
  );
  return useMemo(
    () => ({
      /** Close the dialog, after saving or discarding anything unsaved in it. */
      close: (go: () => void) => void run('close', go),
      /** Switch tabs, after saving or discarding unsaved changes on the tab being left. */
      switchTab: (go: () => void) => void run('tab', go, true),
    }),
    [run],
  );
}

/**
 * Leaving a page in the app (a link, the Back button) while something is unsaved: asks
 * first. Put once inside the router.
 */
export function NavigationGuard() {
  const registry = useContext(RegistryCtx);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => !!registry && currentLocation.pathname !== nextLocation.pathname && registry.dirty().length > 0);
  useEffect(() => {
    if (blocker.state !== 'blocked' || !registry) return;
    void registry.resolve(registry.dirty(), 'leave').then((ok) => (ok ? blocker.proceed() : blocker.reset()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);
  return null;
}

/**
 * A react-hook-form submit that says whether it saved: `onValid` returns true when saved.
 * Use it as the form's submit and as `save` for useUnsavedChanges.
 */
export function formSaver<T extends FieldValues, O = T>(form: UseFormReturn<T, unknown, O>, onValid: (v: O) => Promise<boolean | void>, onInvalid?: (errors: FieldErrors<T>) => void): (e?: { preventDefault?(): void }) => Promise<boolean> {
  return (e) =>
    new Promise<boolean>((resolve) => {
      // Used as a form's onSubmit: stop the browser's own submit, which reloads the page
      // (and, with unsaved changes, makes Chrome ask "Leave site?").
      e?.preventDefault?.();
      void form.handleSubmit(
        async (v) => resolve((await onValid(v)) !== false),
        (errors) => {
          onInvalid?.(errors);
          resolve(false);
        },
      )();
    });
}
